<template>
  <div class="guardianship-form-container">
    <page v-if="currentState.matches('beforeInitialized')">
      <a-text as="h5">Loading...</a-text>
    </page>
    <page
        v-if="currentState.matches('wardList')"
        :title="$t('signUp.addStudents')"
       >

      <cross-district-warning v-if="showCrossDistrictWarning"/>
       
      <div v-if="showCrossDistrictWarning">
        <wards-list-by-district :wards-by-district="wardsByDistrict" @remove-ward="removeWardByMagicCode"/>
      </div>

      <ward-list v-else :wards="machineContext.wards" @remove-ward="removeWardAtIndex"/>

      <a-button variant="link" @click="send('ADD_WARD')">
        <a-icon icon="16:plus" height="16" width="16" />
        {{ $t("signUp.addAnotherStudent") }}
      </a-button>
      <div class="my-5"></div>

      <a-button
          class="sign-up-button"
          v-if="!loggedIn"
          variant="secondary"
          width="100%"
          @click="send('NEXT')"
      >{{ $t("signUp.next") }}</a-button>
      <a-button
          v-if="loggedIn"
          variant="secondary"
          width="100%"
          @click="updateGuardianship"
      >{{ $t("signUp.next") }}</a-button>
    </page>
    <page
        v-if="currentState.matches('magicCodeForm')"
        :show-back-button="machineContext.wards.length > 0"
        :title="addWardTitle"
        @back="send('BACK')"
    >

      <cross-district-warning v-if="showCrossDistrictWarning"/>

      <div v-if="machineContext.wards && showCrossDistrictWarning">
        <wards-list-by-district :wards-by-district="wardsByDistrict" @remove-ward="removeWardByMagicCode"/>
      </div>

      <ward-list v-else-if="machineContext.wards && !showCrossDistrictWarning" :wards="machineContext.wards" @remove-ward="removeWardAtIndex"/>
      <div class="text-left">
        <a-text as="h6">{{ $t("signUp.magicCode") }}</a-text>
        <a-form-input label="" id="magic-code" v-model.trim="magicCode" :show-error="v$.magicCode.$error || !!magicCodeError" :error-message="magicCodeError || $t('signUp.errors.invalidCode')"/>
      </div>
      <a-button
          class="sign-up-button"
          variant="secondary"
          width="100%"
          :disabled="!magicCode"
          @click="addWardByMagicCode(magicCode)"
      >{{ $t("signUp.next") }}</a-button>
    </page>
    <page
        v-if="currentState.matches('guardianForm')"
        :title="guardianFormTitle"
        show-back-button
        @back="send('BACK')"
    >
      <new-guardian-form
          :enable-phone-login="enablePhoneLogin"
          :logged-in-guardian="machineContext.loggedInGuardian"
          :wards="machineContext.wards"
          @guardian-created="onGuardianCreated"/>
    </page>
    <page
        v-if="currentState.matches('existingGuardianList')"
        :title="$t('signUp.existingGuardians')"
        show-back-button
        @back="send('BACK')"
    >
      <existing-guardians :wards="machineContext.wards"/>
      <a-button
       class="sign-up-button"
        id="create-new-button"
        variant="link"
        width="100%"
        @click="send('NEXT')"
      ><a-icon icon="24:plus" height="24" width="24" />{{ $t("signUp.createNew") }}</a-button>
      <div class="my-5"></div>
      <a-button
        class="sign-up-button"
        variant="primary"
        width="100%"
        @click="send('SIGN_IN')">
        {{ $t("signUp.signIn") }}
      </a-button>
    </page>
    <div class="logged-in-footer" v-if="machineContext.loggedInGuardian">
      <div class="my-5"></div>
      Signed in as <strong>{{ loggedInName }}</strong>. <a href="/users/sign_out">Sign out</a>
    </div>
  </div>
</template>

<style lang="scss">
  .logged-in-footer {
    font-size: 0.9em;
    strong {
      font-weight: bold;
    }
  }
  #create-new-button {
    &.a-button {
      .a-button-container {
        justify-content:center;
        gap: 0px;
      }
    }
  }
  .sign-up-button .a-button-container{
    justify-content: space-evenly;
  }
  .sign-up-button {
    border-radius: 8px;
    height: 48px;
    width: 100%;
    display: flex;
    justify-content: center;
  }
  .district-name {
    text-align: left;
    margin-bottom: 8px !important;
  }
</style>

<script>
import { interpret } from 'xstate'
import { machine } from '../../machines/guardian_sign_up'
import { GuardianshipService } from "../../services/GuardianshipService"
import WardList from "./WardList.vue";
import WardsListByDistrict from "./WardsListByDistrict.vue";
import NewGuardianForm from "./NewGuardianForm.vue";
import Page from "./Page.vue";
import ExistingGuardians from "./ExistingGuardians.vue";
import CrossDistrictWarning from "./CrossDistrictWarning.vue";
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default {
  name: 'SignUp',
  components: {ExistingGuardians, Page, NewGuardianForm, WardList, WardsListByDistrict, CrossDistrictWarning},

  props: {
    loggedIn: {
      type: Boolean,
      default: false
    },
    enablePhoneLogin: {
      type: Boolean,
      required: false,
      default: false
    },
  },

  setup() {
    const v$ = useVuelidate()
    return { v$ }
  },

  created() {
    this.startStateMachine()
  },

  async mounted() {
    this.$analytics?.init();
    const schoolClientId = document.getElementById('school-client-id')?.value;
    this.clientId = schoolClientId !== 'false' ? schoolClientId : undefined;
    this.accessMethod = this.clientId ? 'subdomain' : 'main'
    let guardian = null
    const wards = await this.wardsFromQueryString()
    if (this.loggedIn) {
      guardian = await this.getLoggedInGuardian()
    }
    this.send({type: 'INITIALIZE', guardian, wards})
    this.trackMagicCodeProcessStarted();
  },

  validations() {
    return {
      magicCode: { required }
    }
  },

  computed: {
    showCrossDistrictWarning() {
      return new Set(this.machineContext.wards.map((w) => w.client_id)).size > 1
    },
    plusRoundIcon(){
      return new URL("../../../assets/images/icons/plus-round.svg", import.meta.url).href
    },
    addWardTitle() {
      return this.machineContext.wards.length ? this.$t('signUp.addAnotherStudent') : this.$t('signUp.addStudents')
    },
    guardianFormTitle() {
      return this.loggedIn ? this.$t('signUp.yourAccount') : this.$t('signUp.accountInfo')
    },
    loggedInName() {
      const guardian = this.machineContext.loggedInGuardian
      return guardian ? `${guardian.given_name} ${guardian.family_name}` : ""
    },
    wardsByDistrict(){
      return this.machineContext.wards.reduce((acc, ward) => {
        acc[ward.client_name] = [...(acc[ward.client_name] || []), ward]
        return acc
      }, {})
    }
  },
  watch: {
    showCrossDistrictWarning(newVal) {
      if (newVal) {
        this.trackCrossDistrictWarningShown()
      }
    },
    magicCode() {
      this.magicCodeError = ''
    },
    currentState: {
      handler(newState, oldState) {
        if (newState.matches('magicCodeForm') && !oldState?.matches('magicCodeForm')) {
          this.magicCodeError = ''
        }
      }
    }
  },
  data() {
    return {
      stateMachine: interpret(machine),
      currentState: machine.initialState,
      machineContext: machine.context,
      magicCode: '',
      magicCodeError: '',
      guardianFormInvalid: false,
      clientId: '',
      accessMethod: '',
    }
  },

  methods: {
    send(event) {
      this.stateMachine.send(event);
    },
    startStateMachine() {
      this.stateMachine
          .onTransition((state) => {
            // Update the current state component data property with the next state
            this.currentState = state;
            // Update the context component data property with the updated context
            this.machineContext = state.context;
          })
          .start();
    },
    wardsFromQueryString() {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = Array.from(urlSearchParams.entries());
      let magic_codes = new Set()

      params.forEach( (param) => {
        if (param[0].endsWith("[magic_code]")) {
          magic_codes.add(param[1])
        }
      })

      return this.getWards(Array.from(magic_codes))
    },
    async addWardByMagicCode(code) {
      this.v$.$touch()
      if (this.v$.$invalid) {
        this.trackMagicCodeValidationFailure('required')
        return
      }
      const existingWard = this.machineContext.wards.find(w => w.magic_code === code)
      if (existingWard) {
        this.magicCodeError = `You have already added ${existingWard.name} with magic code "${code}".`
        this.trackMagicCodeValidationFailure('already existing ward')
        return
      }
      this.magicCodeError = ''
      try {
        const ward = await GuardianshipService.getWard(code)
        this.send({type: 'WARD_ADDED', ward })
        this.magicCode = ''
        this.magicCodeError = ''
        this.v$.$reset();
        this.trackMagicCodeValidationSuccess(ward.client_id);
      } catch (err) {
        this.magicCodeError = err.error
        this.trackMagicCodeValidationFailure(err.error)
      }
    },
    removeWardAtIndex(index) {
      this.send({type: 'REMOVE_WARD', index: index})
    },
    removeWardByMagicCode(ward) {
      const index = this.machineContext.wards.findIndex(w => w.magic_code === ward.magic_code)
      this.send({type: 'REMOVE_WARD', index: index})
    },
    async getWards(magicCodes) {
      const wards = []
      try {
        const wardResults = await GuardianshipService.getWards(magicCodes)
        wardResults.filter(res => res.status === "rejected").forEach(rejected => {
          this.$notify({
            group: "default",
            type: "error",
            duration: 5000,
            text: rejected.reason.error
          });
        })
        wardResults.filter(res => res.status === "fulfilled").forEach(fulfilled => {
          wards.push(fulfilled.value)
        })
      } catch (rejected) {
        this.$notify({
          group: "default",
          type: "error",
          duration: 5000,
          text: this.$t("signUp.errors.studentNotFound")
        });
      }
      return wards
    },
    async onGuardianCreated(guardian) {
      await this.trackSignUpCompleted();
      this.send({type: 'GUARDIAN_CREATED', guardian: guardian})
    },
    async updateGuardianship() {
      try {
        const response = await GuardianshipService.update(this.machineContext.wards)
        this.send({type: 'GUARDIAN_UPDATED', guardian: response.data})
      } catch (err) {
        console.error(err)
        this.$notify({
          group: "default",
          type: "error",
          duration: 5000,
          text: this.$t("signUp.errors.updatingGuardianship")
        });
      }
    },
    async getLoggedInGuardian() {
      let guardian = {}
      try {
        guardian = await GuardianshipService.getLoggedInGuardian()
      } catch (e) {
        console.error(e, e.response)
        this.$notify({
          group: "default",
          type: "error",
          duration: 5000,
          text: this.$t("signUp.errors.userNotFound")
        });
      }
      return guardian
    },
    async trackMagicCodeProcessStarted() {
      await this.$analytics?.track('magic_code_process_started', {
        access_method: this.accessMethod,
        client_id: this.clientId,
      });
    },
    async trackMagicCodeValidationSuccess(wardClientId) {
      await this.$analytics?.track('magic_code_validation_success', {
        access_method: this.accessMethod,
        client_id: this.clientId || wardClientId,
        magic_code_type: this.showCrossDistrictWarning ? 'multi_district' : 'single_district',
      });
    },
    async trackMagicCodeValidationFailure(failureReason) {
      await this.$analytics?.track('magic_code_validation_failure', {
        access_method: this.accessMethod,
        client_id: this.clientId,
        failure_reason: failureReason,
      });
    },
    async trackSignUpCompleted(){
      await this.$analytics?.track('sign_up_completed', {
        access_method: this.accessMethod,
        client_id: this.clientId,
        is_multi_district: this.showCrossDistrictWarning,
      });
    },
    async trackCrossDistrictWarningShown() {
      const clientIds = new Set(this.machineContext.wards.map((w) => w.client_id))
      await this.$analytics?.track('cross_district_warning_shown', {
        client_ids: Array.from(clientIds),
      });
    }
  }
}
</script>
